import { apiClient } from "./scholarships";

/**
 * Client for the abroad loans API (the Loans service, openapi/abroad-loans-v1.yaml): HEC's calls
 * and programmes, students' applications and awards, evaluators, decisions and BRD. Same `{ data }`
 * calls and problems as the scholarships client; this API versions in the body, not with ETags.
 */
export const ABROAD_LOANS_API = `${process.env.REACT_APP_BASE_URL}hec/abroad-loans/`;

export const abroadLoansApi = apiClient(ABROAD_LOANS_API);

// One uploaded file, as the API's multipart endpoints take it
const fileBody = (file) => {
  const body = new FormData();
  body.append("file", file);
  return body;
};

// The HEC Admin's programme catalogue and calls; the DG and the Minister read them
export const abroadAdminApi = {
  programmes: (params) => abroadLoansApi.get("admin/programmes", { params }),
  createProgramme: (body) => abroadLoansApi.post("admin/programmes", body),
  changeProgramme: (id, body) => abroadLoansApi.put(`admin/programmes/${id}`, body),
  calls: (params) => abroadLoansApi.get("admin/calls", { params }),
  call: (id) => abroadLoansApi.get(`admin/calls/${id}`),
  createCall: (kind, details) => abroadLoansApi.post("admin/calls", { kind, details }),
  changeCall: (id, details) => abroadLoansApi.put(`admin/calls/${id}`, details),
  offer: (id, programmes, levels) => abroadLoansApi.put(`admin/calls/${id}/programmes`, { programmes, levels }),
  publish: (id) => abroadLoansApi.post(`admin/calls/${id}/publish`),
  cancel: (id, reason) => abroadLoansApi.post(`admin/calls/${id}/cancel`, { reason }),
};

// A call's review committee, scores and award list: the HEC Admin runs them, the DG makes the list too, the Minister reads
export const abroadReviewApi = {
  committee: (callId) => abroadLoansApi.get(`admin/calls/${callId}/evaluators`),
  assign: (callId, evaluatorId) => abroadLoansApi.post(`admin/calls/${callId}/evaluators`, { evaluatorId }),
  unassign: (callId, evaluatorId) => abroadLoansApi.delete(`admin/calls/${callId}/evaluators/${evaluatorId}`),
  scores: (callId) => abroadLoansApi.get(`admin/calls/${callId}/applications`),
  application: (id) => abroadLoansApi.get(`admin/applications/${id}`),
  document: (id, documentId) => abroadLoansApi.get(`admin/applications/${id}/documents/${documentId}`, { responseType: "blob" }),
  legacyDocument: (id, documentId) => abroadLoansApi.get(`admin/applications/${id}/legacy-documents/${documentId}`, { responseType: "blob" }),
  reopen: (id, evaluationId) => abroadLoansApi.post(`admin/applications/${id}/evaluations/${evaluationId}/reopen`),
  awardList: (callId) => abroadLoansApi.get(`admin/calls/${callId}/award-list`),
  createAwardList: (callId) => abroadLoansApi.post(`admin/calls/${callId}/award-list`),
  saveEntries: (callId, version, entries) => abroadLoansApi.put(`admin/calls/${callId}/award-list/entries`, { version, entries }),
  finalise: (callId, version) => abroadLoansApi.post(`admin/calls/${callId}/award-list/finalise`, { version }),
};

// A committee member's side: the calls they sit on, consent, conflicts and their marks
export const abroadEvaluatorApi = {
  calls: () => abroadLoansApi.get("evaluator/calls"),
  queue: (callId) => abroadLoansApi.get(`evaluator/calls/${callId}/applications`),
  consent: (callId) => abroadLoansApi.post(`evaluator/calls/${callId}/consent`),
  removeApplicant: (id, body) => abroadLoansApi.put(`evaluator/applications/${id}/conflict`, body),
  restoreApplicant: (id) => abroadLoansApi.delete(`evaluator/applications/${id}/conflict`),
  application: (id) => abroadLoansApi.get(`evaluator/applications/${id}`),
  document: (id, documentId) => abroadLoansApi.get(`evaluator/applications/${id}/documents/${documentId}`, { responseType: "blob" }),
  legacyDocument: (id, documentId) => abroadLoansApi.get(`evaluator/applications/${id}/legacy-documents/${documentId}`, { responseType: "blob" }),
  saveMarks: (id, body) => abroadLoansApi.put(`evaluator/applications/${id}/evaluation`, body),
  submitMarks: (id) => abroadLoansApi.post(`evaluator/applications/${id}/evaluation/submit`),
};

// A student's side: open calls, their applications, documents and submitting
export const abroadStudentApi = {
  publicCalls: () => abroadLoansApi.get("public/calls"),
  calls: () => abroadLoansApi.get("calls"),
  call: (id) => abroadLoansApi.get(`calls/${id}`),
  consent: () => abroadLoansApi.get("consent"),
  applications: () => abroadLoansApi.get("applications"),
  application: (id) => abroadLoansApi.get(`applications/${id}`),
  start: (callId, programmeId) => abroadLoansApi.post("applications", { callId, programmeId }),
  saveForm: (id, form) => abroadLoansApi.put(`applications/${id}/form`, form),
  upload: (id, requirementId, file) => abroadLoansApi.put(`applications/${id}/documents/${requirementId}`, fileBody(file)),
  removeDocument: (id, documentId) => abroadLoansApi.delete(`applications/${id}/documents/${documentId}`),
  document: (id, documentId) => abroadLoansApi.get(`applications/${id}/documents/${documentId}`, { responseType: "blob" }),
  legacyDocument: (id, documentId) => abroadLoansApi.get(`applications/${id}/legacy-documents/${documentId}`, { responseType: "blob" }),
  submit: (id, consentVersion) => abroadLoansApi.post(`applications/${id}/submit`, { consentGiven: true, consentVersion }),
  withdraw: (id, reason) => abroadLoansApi.post(`applications/${id}/withdraw`, reason ? { reason } : {}),
  // An unsent application is cancelled; a sent one goes back to a draft until the deadline
  cancel: (id) => abroadLoansApi.post(`applications/${id}/cancel`),
  reopen: (id) => abroadLoansApi.post(`applications/${id}/reopen`),
  letter: (id) => abroadLoansApi.get(`applications/${id}/letter`, { responseType: "blob" }),
  award: (id) => abroadLoansApi.get(`applications/${id}/award`),
  answer: (id, body) => abroadLoansApi.post(`applications/${id}/award/answer`, body),
  saveTravel: (id, body) => abroadLoansApi.put(`applications/${id}/award/travel`, body),
  submitTravel: (id) => abroadLoansApi.post(`applications/${id}/award/travel/submit`),
  uploadVisa: (id, file) => abroadLoansApi.put(`applications/${id}/award/visa`, fileBody(file)),
  // `which` is "visa" or "ticket"
  awardFile: (id, which) => abroadLoansApi.get(`applications/${id}/award/${which}`, { responseType: "blob" }),
};

// BRD's side of an accepted award: the ticket, payments and messages; HEC reads them
export const abroadBrdApi = {
  awards: () => abroadLoansApi.get("brd/awards"),
  award: (id) => abroadLoansApi.get(`brd/awards/${id}`),
  // `body` is FormData: airline, flightNumber, departureAt and the file
  issueTicket: (id, body) => abroadLoansApi.put(`brd/awards/${id}/ticket`, body),
  recordPayment: (id, body) => abroadLoansApi.post(`brd/awards/${id}/payments`, body),
  removePayment: (id, paymentId) => abroadLoansApi.delete(`brd/awards/${id}/payments/${paymentId}`),
  awardFile: (id, which) => abroadLoansApi.get(`brd/awards/${id}/${which}`, { responseType: "blob" }),
};

// Awards on their way through the DG and the Minister: the HEC Admin, the DG and the Minister decide in turn
export const abroadDecisionsApi = {
  list: (params) => abroadLoansApi.get("decisions", { params }),
  decide: (applicationIds, action, text) => abroadLoansApi.post("decisions", { applicationIds, action, ...text }),
  changeAmounts: (id, approvedAmounts, reason) => abroadLoansApi.put(`decisions/${id}/approved-amounts`, { approvedAmounts, ...(reason && { reason }) }),
  letter: (id) => abroadLoansApi.get(`admin/applications/${id}/letter`, { responseType: "blob" }),
};
